import { useState, useCallback, type ReactNode } from "react";
import { AnimatePresence } from "framer-motion";
import type { Categorization } from "../types/transaction";
import { ApiClientError } from "../types/api";
import { refusalSentence } from "../hooks/useServerAnswer";
import { splitTransaction, type ConfirmAllTarget } from "../api/transactions";
import { useAlternatives } from "../hooks/useAlternatives";
import { useCardExit } from "../hooks/useCardExit";
import { useConfirmAll } from "../hooks/useConfirmAll";
import { useDeckAnswers } from "../hooks/useDeckAnswers";
import { useRowSteps } from "../hooks/useRowSteps";
import { TransactionCard } from "./TransactionCard";
import { ErrorMessage } from "./ErrorMessage";
import { AccountSelector } from "./AccountSelector";
import { SplitModal } from "./SplitModal";
import { CelebrationScreen } from "./review/CelebrationScreen";
import { ConfirmAllButton } from "./review/ConfirmAllButton";
import { FenceQuestion } from "./review/FenceQuestion";
import { heldAccountLast6 } from "./review/heldRefusal";
import { RedoButton } from "./review/RedoButton";
import { ReviewedSection } from "./review/ReviewedSection";
import { AnimatedProgress } from "./ui/AnimatedProgress";
import { CountUp } from "./ui/CountUp";

interface TransactionStackProps {
  items: Categorization[];
  accounts: string[];
  clientId: number;
  onUpdated: () => void;
  total: number;
  /** The list response's ledger_readable (D1 amendment 2.8 (6)). */
  ledgerReadable: boolean;
  /** The server's label for an account it names (the pick list's `labels`). */
  labels?: Record<string, string>;
  /**
   * A held account's refusal (409 ACCOUNT_IN_OTHER_BOOKS) on a row: true when
   * a banner already carries its sentence and was brought into view, so the
   * sentence is not printed a second time.
   */
  onHeldRefusal?: (last6: string) => boolean;
}

/**
 * Deck order: categorized cards first by confidence, nulls last; then the
 * uncategorized; a needs-attention card after every other card, so it never
 * blocks the bank's own card on a two-card deck with no skip (2.8 (8)).
 */
function comparePending(a: Categorization, b: Categorization): number {
  if (a.needs_attention !== b.needs_attention) return a.needs_attention ? 1 : -1;
  if (a.status === "uncategorized" && b.status !== "uncategorized") return 1;
  if (b.status === "uncategorized" && a.status !== "uncategorized") return -1;
  return (b.confidence_score ?? -1) - (a.confidence_score ?? -1);
}

/**
 * The shown pending cards confirm-all may book, each with the log line it
 * was built from (2.8 (5)). Leaves out the uncategorized, every card whose
 * `can_redo` is true (one the client's Undo or the support team returned)
 * and every needs-attention card, which the server would refuse.
 */
function confirmAllTargets(pending: Categorization[]): ConfirmAllTarget[] {
  return pending
    .filter((i) => i.status !== "uncategorized" && !i.can_redo && !i.needs_attention)
    .map((i) => ({ id: i.id, expected_last_log_id: i.last_log_id }));
}

function isConflict(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 409;
}

export function TransactionStack({
  items,
  accounts,
  clientId,
  onUpdated,
  total,
  ledgerReadable,
  labels = {},
  onHeldRefusal,
}: TransactionStackProps): ReactNode {
  const [selectorItemId, setSelectorItemId] = useState<number | null>(null);
  // Accounts the user has picked in the selector but not yet finalized, keyed
  // by categorization id. A preview is committed only on confirm/swipe-right.
  const [previewAccounts, setPreviewAccounts] = useState<Record<number, string>>({});
  const [splitItem, setSplitItem] = useState<Categorization | null>(null);
  // Refusal sentences on screen, each kept until the client dismisses it
  // (C18; 2.8 (4)); one entry per distinct sentence.
  const [notices, setNotices] = useState<string[]>([]);
  // Rows the server refused because their account keeps its own books.
  const [heldIds, setHeldIds] = useState<ReadonlySet<number>>(new Set());
  const exit = useCardExit(onUpdated);

  const pendingItems = items
    .filter(
      (i) => (i.status === "pending" || i.status === "uncategorized") && !exit.exitingIds.has(i.id),
    )
    .sort(comparePending);
  const reviewedItems = items.filter((i) => i.status === "confirmed" || i.status === "corrected");
  const confirmTargets = confirmAllTargets(pendingItems);

  const addNotice = useCallback((sentence: string) => {
    setNotices((prev) => (prev.includes(sentence) ? prev : [...prev, sentence]));
  }, []);

  // A refused card or row action: its sentence stays until dismissed, and
  // after EVERY 409 the list is fetched again, so a card another tab already
  // handled does not stay on top answering the same sentence (2.8 (4)). A
  // held account's refusal marks the row and points at the banner carrying
  // the same sentence instead (requirements What 3, Advocate M5).
  const reportRefusal = useCallback(
    (err: unknown, itemId?: number) => {
      const heldLast6 = heldAccountLast6(err);
      if (heldLast6 !== null && itemId !== undefined) {
        setHeldIds((prev) => new Set(prev).add(itemId));
      }
      const pointed = heldLast6 !== null && (onHeldRefusal?.(heldLast6) ?? false);
      if (!pointed) addNotice(refusalSentence(err));
      if (isConflict(err)) onUpdated();
    },
    [addNotice, onUpdated, onHeldRefusal],
  );

  const answers = useDeckAnswers(clientId, exit, onUpdated, reportRefusal);
  const stepRefused = useCallback((err: unknown) => reportRefusal(err), [reportRefusal]);
  const steps = useRowSteps(clientId, items, onUpdated, stepRefused);
  const alternatives = useAlternatives(clientId, selectorItemId, accounts);

  const categorizedCount = total - pendingItems.length;
  const progressPercent = total > 0 ? (categorizedCount / total) * 100 : 0;

  const handleFinalize = useCallback(
    async (itemId: number) => {
      const item = items.find((i) => i.id === itemId);
      // A needs-attention card offers no Confirm, by button or by swipe.
      if (item?.needs_attention) return;
      const previewed = previewAccounts[itemId];
      // Clear any pending preview for this card regardless of branch taken.
      if (previewed !== undefined) {
        setPreviewAccounts((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
      }
      if (previewed !== undefined && item && previewed !== item.account_name) {
        await answers.correct(itemId, previewed);
      } else {
        await answers.confirm(itemId);
      }
    },
    [items, previewAccounts, answers],
  );

  const confirmAll = useConfirmAll(clientId, confirmTargets, onUpdated, (sentences) =>
    sentences.forEach(addNotice),
  );

  const handleSplit = useCallback(
    async (itemId: number, lines: { account_name: string; amount: string }[]) => {
      try {
        await splitTransaction(clientId, itemId, lines);
        setSplitItem(null);
        onUpdated();
      } catch (err) {
        reportRefusal(err);
      }
    },
    [clientId, onUpdated, reportRefusal],
  );

  /** The top card's own question and step: the fence's answers, then Redo. */
  function cardExtra(item: Categorization): ReactNode {
    return (
      <>
        {item.fence && (
          <FenceQuestion
            fence={item.fence}
            items={items}
            disabled={exit.processingIds.has(item.id)}
            onTransfer={() => void answers.transfer(item)}
            onNotATransfer={(other) => void answers.notATransfer(item.id, other)}
            onRefund={() => void answers.refund(item.id)}
            onChooseCategory={() => setSelectorItemId(item.id)}
          />
        )}
        {item.can_redo && (
          <RedoButton
            disabled={steps.isRedoDisabled(item)}
            onRedo={() => void steps.redo(item.id)}
          />
        )}
      </>
    );
  }

  const selectorItem = items.find((i) => i.id === selectorItemId);
  const allDone = pendingItems.length === 0 && total > 0;
  const correctedCount = reviewedItems.filter((i) => i.status === "corrected").length;

  return (
    <div className="transaction-stack-container">
      {/* Progress indicator */}
      {total > 0 && (
        <div className="stack-progress">
          <div className="stack-progress-text">
            <CountUp to={categorizedCount} className="stack-progress-count" />
            <span className="stack-progress-of"> of {total} categorized</span>
          </div>
          <AnimatedProgress value={progressPercent} className="stack-progress-bar" />
        </div>
      )}

      {/* Refusal sentences, each kept until dismissed */}
      <AnimatePresence>
        {notices.map((notice) => (
          <ErrorMessage
            key={notice}
            message={notice}
            onDismiss={() => setNotices((prev) => prev.filter((n) => n !== notice))}
          />
        ))}
      </AnimatePresence>

      {/* Celebration screen: never beside a refusal still on screen */}
      <AnimatePresence>
        {allDone && reviewedItems.length > 0 && notices.length === 0 && (
          <CelebrationScreen allDone={allDone} total={total} correctedCount={correctedCount} />
        )}
      </AnimatePresence>

      {/* Card stack */}
      {pendingItems.length > 0 && (
        <div className="card-stack">
          <AnimatePresence mode="popLayout">
            {pendingItems.slice(0, 2).map((item, index) => (
              <TransactionCard
                key={item.id}
                item={item}
                clientId={clientId}
                previewAccount={previewAccounts[item.id] ?? null}
                isTop={index === 0}
                index={index}
                labels={labels}
                held={heldIds.has(item.id)}
                extra={index === 0 ? cardExtra(item) : null}
                onConfirm={() => void handleFinalize(item.id)}
                onShowAlternatives={() => setSelectorItemId(item.id)}
                onSplit={() => setSplitItem(item)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Confirm all: the shown targets, their count on the button */}
      {confirmTargets.length > 1 && (
        <ConfirmAllButton
          count={confirmTargets.length}
          confirming={confirmAll.confirming}
          onConfirmAll={() => void confirmAll.run().catch((err) => reportRefusal(err))}
        />
      )}

      <ReviewedSection
        items={reviewedItems}
        ledgerReadable={ledgerReadable}
        labels={labels}
        steps={steps}
      />

      {/* Account selector modal */}
      <AnimatePresence>
        {selectorItem && (
          <AccountSelector
            suggestions={alternatives.suggestions}
            allAccounts={alternatives.accounts.length > 0 ? alternatives.accounts : accounts}
            currentAccount={previewAccounts[selectorItem.id] ?? selectorItem.account_name}
            onSelect={(account) => {
              // Two-step correction: preview the choice on the card and close
              // the selector. The write happens only when the user finalizes
              // with swipe-right / Confirm (see handleFinalize). No accidental
              // submit. (Transaction Review Design, Journeys 3 & 6.)
              setPreviewAccounts((prev) => ({
                ...prev,
                [selectorItem.id]: account,
              }));
              setSelectorItemId(null);
            }}
            onClose={() => setSelectorItemId(null)}
          />
        )}
      </AnimatePresence>

      {/* Split modal */}
      <AnimatePresence>
        {splitItem && (
          <SplitModal
            item={splitItem}
            accounts={accounts}
            onClose={() => setSplitItem(null)}
            onSplit={(lines) => void handleSplit(splitItem.id, lines)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
